'use strict';

const _ = require('underscore');
const Backbone = require('backbone');
const PartialPageConfiguration = require('framework/domain/partial-page/partial-page-configuration');
const PartialPage = require('framework/domain/partial-page/partial-page');
const PartialPagination = require('framework/domain/partial-page/partial-pagination');

class PartialPageCollection extends Backbone.Collection {
    /**
     * 時系列コレクションを最新部分・途中部分という形で切り出して扱うための Collection クラス
     *
     * 時系列コレクションの特定の時間範囲のコレクションは PartialPage クラスが表現する。
     * このクラスは、その PartialPage クラスの集合を取り扱う。
     * 取り扱うクラスによって変化する振る舞いは、 PartialPageConfiguration の継承先で定義する。
     *
     * このコレクションで扱う並び順は日時昇順のみ、ページングの順方向は日時降順に限定する。
     * APIの順方向と、 PartialPage, PartialPageCollection で取り扱う順方向は向きが異なっている点に注意すること。
     *
     *                             collection[0, .....last]
     *   ------------------------------------[------------]--> t
     *                  [ (page3) ][ (page2) ][ ...3,2,1,0]  <--API応答
     *                        <-------- ページングの順方向
     *
     * @param models {PartialPage[]}
     * @param options {Object}
     * @param options.configuration {PartialPageConfiguration}
     */
    constructor(models, options) {
        super(models, options);

        this._configuration = options && options.configuration;
        this._firstPage = null;
        this._totalCount = null;

        if (!(this._configuration instanceof PartialPageConfiguration)) {
            throw new Error(`Invalid parameter of configuration: ${this._configuration}`);
        }
    }

    /**
     * この PartialPageCollection の振る舞いを定義する Configuration オブジェクト
     *
     * @return {PartialPageConfiguration}
     */
    configuration() {
        return this._configuration;
    }

    /**
     * PartialPageの保持する合計アイテム数
     *
     * @return {number}
     */
    totalItemCount() {
        return _.reduce(
            this.models,
            (sum, partialPage) => {
                return partialPage.itemCount() + sum;
            },
            0
        );
    }

    /**
     * この PartialPageCollection の扱う対象 Item の全体の個数を返す
     *
     * まだ、一度も fetch しておらず、全体数が不明の場合には null が返る。
     *
     * @return {number|null}
     */
    getTotalCount() {
        return this._totalCount;
    }

    /**
     * この PartialPageCollection の扱う対象サイズを increment だけ増加します
     *
     * @param increment {number}
     */
    addTotalCount(increment) {
        this._totalCount += increment;
        this.trigger('change:totalCount');
    }

    /**
     * この PartialPageCollection の扱う対象サイズを設定します
     *
     * @param number {number}
     */
    setTotalCount(number) {
        this._totalCount = number;
        this.trigger('change:totalCount');
    }

    /**
     * 部分ページを追加する
     *
     * @param partialPage {PartialPage}
     * @param options {Object}
     * @return {PartialPage|null}
     */
    addPartialPage(partialPage, options={}) {
        // 引数に渡された partialPage と重なっている既存のページを取得する
        const overlappedPage = _.find(this.models, (/**PartialPage*/page) => {
            return page.isOverlapped(partialPage);
        });

        // 既存ページと重なっているならば、小さい側 or 大きい側のはみ出している部分を追加する
        if (overlappedPage) {
            const smallSidePage = overlappedPage.buildSmallSideSplitPartialPage(partialPage);
            const biggerSidePage = overlappedPage.buildBiggerSideSplitPartialPage(partialPage);

            let newPagination = overlappedPage.pagination();
            if (smallSidePage) {
                newPagination = newPagination.clearNextSide();
                this.addPartialPage(smallSidePage, options);
            }
            if (biggerSidePage) {
                newPagination = newPagination.clearPrevSide();
                this.addPartialPage(biggerSidePage, options);
            }

            // partialPageのpagination情報が存在しているならば、重なっている既存ページのpagination情報を上書きして良い
            if (partialPage.pagination().hasMore) {
                overlappedPage.setPagination(newPagination);
            }

            // 挿入しようとしているページが既存ページと重なっているが、小さい側・大きい側のはみ出し部分が無い場合、
            // 挿入しようとしているページの隣接ページ情報を既存ページに上書きする
            if (!smallSidePage && !biggerSidePage) {
                const sourcePageInBiggerSide = partialPage.getBiggerSidePartialPage();
                if (sourcePageInBiggerSide) {
                    sourcePageInBiggerSide.setSmallSidePartialPage(overlappedPage);
                    overlappedPage.setBiggerSidePartialPage(sourcePageInBiggerSide);
                    overlappedPage.setPagination(newPagination.clearPrevSide());
                }

                const sourcePageInSmallSide = partialPage.getSmallSidePartialPage();
                if (sourcePageInBiggerSide && sourcePageInSmallSide) {
                    sourcePageInBiggerSide.setBiggerSidePartialPage(overlappedPage);
                    overlappedPage.setSmallSidePartialPage(sourcePageInSmallSide);
                    overlappedPage.setPagination(newPagination.clearNextSide());
                }
            }

            return null;
        }

        this._addPartialPage(partialPage, options);

        return partialPage
    }

    /**
     * 部分ページを適切な位置に追加する
     *
     * @param partialPage {PartialPage}
     * @param options {Object}
     * @private
     */
    _addPartialPage(partialPage, options) {
        // 適切な挿入場所を予め計算してから add する
        // (addしてからsortすると、 CollectionViewの並び順と連動しないため && resetをなるべく回避したいので)
        options.at = _.sortedIndex(
            _.map(this.models, (/**PartialPage*/page) => { return page.min(); }),
            partialPage.min()
        );

        this.add(partialPage, options);

        // 追加しようとしている部分ページが、大小どちらかで別のページに接しているときには、
        // その接しているページから追加しようとしているページへの参照を追加する (相互参照できるようにする)
        //
        // PartialPageをビルドした時点では、副作用が少なくなるように一方向の参照に留めていた。
        // Collection追加した後のタイミングで前後のページが連なるように（相互参照になるように）更新する。
        // (Collection追加後じゃないと、 seriesOfPages() で辿れないタイミングがでてしまうので処理順序に注意)
        if (partialPage.getBiggerSidePartialPage()) {
            partialPage.getBiggerSidePartialPage().setSmallSidePartialPage(partialPage);
        }
        if (partialPage.getSmallSidePartialPage()) {
            partialPage.getSmallSidePartialPage().setBiggerSidePartialPage(partialPage);
        }

        if (!this._firstPage) {
            this._firstPage = partialPage;
        }
    }

    /**
     * この PartialPageCollection に対して、新しいページを作成してモデルを追加する
     *
     * @param item {Backbone.Model}
     * @param options {Object}
     * @param options.allowIndividualPageInMiddle {boolean}
     */
    addItem(item, options = {}) {
        const latestPage = this.getLatestPage();
        const insertNumber = this._configuration.modelToNumber(item);

        if (!latestPage) {
            // 最新ページが存在しない場合には、新しくページを作成して挿入する。
            this.addItems([item], PartialPagination.buildEmpty());
        } else if (latestPage && latestPage.max() < insertNumber) {
            // 最新ページが存在し、その最新ページの最大値よりも追加対象の item の number が大きければ、そのページに追加する
            latestPage.addItem(item);
        } else {
            // 最新ページが存在しない場合、あるいは、中間に差し込むべき要素であり、
            // 中間部分への孤立要素の追加が許可されていれば、
            // 新しいページを作成してCollectionに追加する
            if (options && options.allowIndividualPageInMiddle) {
                this.addItems([item], PartialPagination.buildEmpty());
            }
        }
    }

    /**
     * この PartialPageCollection に対して、新しいページを作成してモデルを追加する
     *
     * @param items {Backbone.Model[]}
     * @param pagination {PartialPagination|null}
     */
    addItems(items, pagination=null) {
        const partialPage = this.buildPartialPageByItems(items, pagination);
        this.addPartialPage(partialPage);
    }

    /**
     * この PartialPageCollection の configuration を利用した、新しい PartialPage を作成して返す
     *
     * @param item {Backbone.Model}
     */
    buildPartialPageByItem(item) {
        return this.buildPartialPageByItems([item], PartialPagination.buildEmpty());
    }

    /**
     * この PartialPageCollection の configuration を利用した、新しい PartialPage を作成して返す
     *
     * @param items {Backbone.Model[]}
     * @param pagination {PartialPagination|null}
     */
    buildPartialPageByItems(items, pagination=null) {
        if (items.length === 0) {
            return null;
        }

        return new PartialPage(
            {
                configuration: this._configuration,
                pagination: pagination || PartialPagination.buildEmpty(),
            },
            {
                items: items,
            }
        );
    }

    /**
     * 最初の部分ページを返す
     *
     * @return {PartialPage|null}
     */
    getFirstPage() {
        return this._firstPage;
    }

    /**
     * keyを指定して、そのKeyを含む部分ページがローカルに存在していれば、それを返す
     *
     * @param key {Key}
     * @return {PartialPage|null}
     */
    getPageByKey(key) {
        throw new Error(`Not Implemented. (key=${key})`);
    }

    /**
     * cursor, direction, limit を指定して、部分ページを取得する
     *
     * @param cursor {string|null}
     * @param direction {string|null} next or prev
     * @param limit {number|null}
     * @return {Promise<PartialPage|null>}
     * @private
     */
    _fetchPage(cursor=null, direction=null, limit=null) { // eslint-disable-line no-unused-vars
        throw new Error('Not Implemented.');
    }

    /**
     * Keyを指定して、そのKeyを含む部分ページを取得する
     *
     * @param key {Key}
     * @param limit {number|null}
     * @return {Promise<PartialPage>}
     * @private
     */
    _fetchPageByItemKey(key, limit=null) { // eslint-disable-line no-unused-vars
        throw new Error('Not Implemented.');
    }

    /**
     * 最初の部分ページを取得する
     *
     * @return {Promise<PartialPageCollection>}
     */
    fetchFirstPage() {
        if (this.getFirstPage()) {
            return new Promise(resolve => {
                resolve(this);
            });
        }

        return new Promise((resolve, reject) => {
            const cursor = null;
            const direction = "next";
            const limit = null;

            this._fetchPage(cursor, direction, limit)
                .then(partialPage => {
                    if (partialPage) {
                        this.addPartialPage(partialPage);
                    }
                    resolve(this);
                })
                .catch(reject);
        });
    }

    /**
     * 指定したKeyを含む PartialPage を取得する
     *
     * @param key {Key}
     * @return {Promise<PartialPageCollection>}
     */
    fetchPageByItemKey(key) {
        if (this.getPageByKey(key)) {
            return new Promise(resolve => {
                resolve(this);
            });
        }

        return new Promise((resolve, reject) => {
            this._fetchPageByItemKey(key)
                .then(partialPage => {
                    this.addPartialPage(partialPage);
                    resolve(this);
                })
                .catch(reject);
        });
    }

    /**
     * 最新のページを返す
     *
     * @return {PartialPage|null}
     */
    getLatestPage() {
        if (this.isEmpty()) {
            return null;
        }

        return this.max(partialPage => {
            return partialPage.max();
        });
    }
}

module.exports = PartialPageCollection;



// WEBPACK FOOTER //
// ./framework/domain/partial-page/partial-page-collection.js